import type { ProductionOrderComponentDTO, ProductionOrderOperationDTO } from "../../../api/floor";
import type { ProductionOrderDTO } from "../../../api/production";
import type { ProgressTone } from "../../../components/ProgressBar";

/**
 * Pure helpers for a lot's page: the component × process matrix, how a step
 * reads, and whether a lot is late. No fetching here.
 */

/** The floor's processes in the order work flows; anything else after, by code. */
const PROCESS_ORDER = ["CUT", "EDGE", "MACHINING", "PAINT", "ASSEMBLY", "INSPECTION"];

export interface ProcessColumn {
  code: string;
  name: string;
}

export interface MatrixRow {
  component: ProductionOrderComponentDTO;
  /** The component's step for each process code; a component may skip a process entirely. */
  steps: Map<string, ProductionOrderOperationDTO>;
}

/** Columns: the processes some component of the lot goes through, in floor order. */
export function processColumns(steps: ProductionOrderOperationDTO[]): ProcessColumn[] {
  const seen = new Map<string, string>();
  for (const step of steps) {
    if (step.production_order_component_id == null || !step.process_code) continue;
    if (!seen.has(step.process_code)) seen.set(step.process_code, step.process_name ?? step.process_code);
  }
  const rank = (code: string) => {
    const index = PROCESS_ORDER.indexOf(code);
    return index < 0 ? PROCESS_ORDER.length : index;
  };
  return [...seen.entries()]
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => rank(a.code) - rank(b.code) || a.code.localeCompare(b.code));
}

export function matrixRows(components: ProductionOrderComponentDTO[], steps: ProductionOrderOperationDTO[]): MatrixRow[] {
  const byComponent = new Map<number, Map<string, ProductionOrderOperationDTO>>();
  for (const step of steps) {
    if (step.production_order_component_id == null || !step.process_code) continue;
    const row = byComponent.get(step.production_order_component_id) ?? new Map<string, ProductionOrderOperationDTO>();
    // A routing could name a process twice; the first one is shown, the others are in the step list.
    if (!row.has(step.process_code)) row.set(step.process_code, step);
    byComponent.set(step.production_order_component_id, row);
  }
  return [...components]
    .sort((a, b) => a.sequence - b.sequence)
    .map((component) => ({ component, steps: byComponent.get(component.id) ?? new Map() }));
}

/** An order's own steps, on no component: an assembly order's kit, foam, cornières and packing. */
export function lotSteps(steps: ProductionOrderOperationDTO[]): ProductionOrderOperationDTO[] {
  return steps.filter((step) => step.production_order_component_id == null);
}

/** Done over what the step needs, in the step's own unit (cutboards for a nested part at Cut and Edge). */
export function stepShare(step: ProductionOrderOperationDTO): number {
  if (step.status === "DONE") return 1;
  const required = Number(step.quantity_required_counted ?? step.quantity_required);
  if (!(required > 0)) return 0;
  return Math.max(0, Math.min(1, Number(step.quantity_done ?? 0) / required));
}

/**
 * How a step reads at a glance: done green, skipped hatched, missing parts
 * red, stalled amber, under way blue, not started grey.
 */
export function stepTone(step: ProductionOrderOperationDTO): ProgressTone {
  if (step.status === "DONE") return "done";
  if (step.status === "SKIPPED") return "skipped";
  if (Number(step.quantity_missing ?? 0) > 0) return "risk";
  if (step.is_stalled) return "warn";
  if (step.status === "ACTIVE") return "active";
  return "idle";
}

/** True when the step counts cutboards rather than parts. */
export function countsCutboards(step: ProductionOrderOperationDTO): boolean {
  return step.counting_unit === "CUTBOARD" && Number(step.quantity_required_counted ?? 0) !== Number(step.quantity_required);
}

/** Today as YYYY-MM-DD in local time (never toISOString, which is UTC). */
export function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** A lot past its due date that is not finished. */
export function isLate(lot: Pick<ProductionOrderDTO, "due_date" | "status">): boolean {
  return lot.due_date != null && lot.due_date < today() && lot.status !== "COMPLETED" && lot.status !== "CANCELLED";
}

/** Whole days from today to the due date: negative when late, null with no due date. */
export function daysToDue(lot: Pick<ProductionOrderDTO, "due_date">): number | null {
  if (!lot.due_date) return null;
  const due = Date.parse(`${lot.due_date}T00:00:00`);
  const start = Date.parse(`${today()}T00:00:00`);
  return Math.round((due - start) / 86_400_000);
}

/** A timestamp as a short local date and time. */
export function when(value: string | null | undefined, language: string): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(language, { dateStyle: "medium", timeStyle: "short" });
}
